import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import {
  FbInnerContainer,
  FbIconButton,
  FbInput,
  FbTooltip,
  FbGrid,
  FbTypeography,
  FbBox,
} from "@flexibees/core";
import { Table, Space } from "antd";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import SearchIcon from "@material-ui/icons/Search";
import BackButton from "@material-ui/icons/ArrowBack";
import "antd/dist/antd.css";
import * as configureAction from "../../actions/configureAction";
import { getHighlightedText } from "../../utils/textHighlighter";
import { columnTitle } from "../../common/column";
import { useConfigStyle } from "../../common/styles";
import ConfigurationModal from "../../components/configure/ConfigCommon";

//  Extracted outside — fixes "Do not define components during render"
const ExpandIcon = ({ expanded, onExpand, record, onToggle }) => {
  const handleClick = (e) => {
    onToggle(record.id);
    onExpand(record, e);
  };

  return expanded ? (
    <ExpandLessIcon onClick={handleClick} />
  ) : (
    <ExpandMoreIcon onClick={handleClick} />
  );
};

//  Extracted outside — fixes "Do not define components during render"
const ExpandedRowRender = ({ record, classes, searchText }) => (
  <FbGrid container>
    <FbGrid item style={{ paddingLeft: "13%" }}>
      <Space size={[20, 20]} wrap>
        {record.skills
          .slice(5, record.skills.length)
          .map((tag, index) => (
            <FbTooltip
              key={tag.tag_name ?? index}
              arrow
              className={classes.textTransform_none}
            >
              <FbTypeography
                variant={"caption"}
                className={[classes.textTransform_none]}
              >
                {searchText.length > 2
                  ? getHighlightedText(tag.tag_name, searchText)
                  : tag.tag_name}
              </FbTypeography>
            </FbTooltip>
          ))}
      </Space>
    </FbGrid>
  </FbGrid>
);

export default function FunctionRoleSkillMappingPage() {
  const classes = useConfigStyle();
  const dispatch = useDispatch();
  const history = useHistory();
  const queryParameters = new URLSearchParams(window.location.search);
  const name = queryParameters.get("name");
  const id = queryParameters.get("id");
  const isLoading = useSelector((state) => state.config.fetchingData);
  const rollskillMappingData = useSelector(
    (state) => state.config.rollSkillMappingData,
  );

  const [showAddConfigModal, setshowAddConfigModal] = useState(false);
  const [showMoreTags, setshowMoreTags] = useState([]);
  const [titleTag, setTitleTag] = useState({});
  const [addEditConfig, setaddEditConfig] = useState(true);
  const [searchText, setsearchText] = useState("");

  const handleChange = (key) => async (value) => {
    setsearchText(value);
    if (value.length > 2) {
      dispatch(configureAction.search_rollskillMapping(value, id));
    }
  };

  useEffect(() => {
    if (searchText === undefined || searchText === "") {
      setsearchText("");
      dispatch(configureAction.get_rolls_skill_mapping(id));
    }
  }, [searchText]); // eslint-disable-line react-hooks/exhaustive-deps

  const create_configutation = (title, tags, configListID) => {
    let tags_Array = tags.split(",");
    setTimeout(() => {
      dispatch(
        configureAction.create_configuration(
          title,
          tags_Array,
          configListID,
          refreshTable,
        ),
      );
    }, 200);
  };

  const refreshTable = () => {
    setTitleTag({});
    setaddEditConfig(false);
    setshowAddConfigModal(false);
    dispatch(configureAction.get_configuration());
  };

  const addDeleteShowMoreTags = (id) => {
    let moreTags = showMoreTags;
    if (moreTags.includes(id)) {
      let index = moreTags.indexOf(id);
      if (index > -1) {
        moreTags.splice(index, 1);
      }
    } else {
      moreTags.push(id);
    }
    setshowMoreTags(moreTags);
  };

  //  Stable reference via useCallback
  const expandIcon = useCallback(
    ({ expanded, onExpand, record }) => (
      <ExpandIcon
        expanded={expanded}
        onExpand={onExpand}
        record={record}
        onToggle={addDeleteShowMoreTags}
      />
    ),
    [addDeleteShowMoreTags],
  );

  //  Stable reference via useCallback
  const expandedRowRender = useCallback(
    (record) => (
      <ExpandedRowRender
        record={record}
        classes={classes}
        searchText={searchText}
      />
    ),
    [classes, searchText],
  );

  const columns = [
    {
      title: columnTitle(classes, "Role"),
      dataIndex: "role",
      key: "role",
      width: 200,
      render: (role) => (
        <div className={classes.tags}>
          <FbTypeography
            variant={"body1"}
            className={[classes.textTransform_none]}
          >
            {searchText.length > 2
              ? getHighlightedText(role, searchText)
              : role}
          </FbTypeography>
        </div>
      ),
    },
    {
      title: columnTitle(classes, "Skills"),
      fixed: true,
      width: 800,
      dataIndex: "skills",
      key: "skills",
      render: (skill) => {
        return (
          <Space size={[20, 20]} wrap>
            {skill.length <= 0 ? (
              <FbTypeography
                variant={"caption"}
                className={[classes.textTransform_none]}
              >
                {`No Tags to display`}
              </FbTypeography>
            ) : null}
            {skill.slice(0, 5).map((tag) => (
              <FbTooltip
                key={tag.tag_name}
                disableHoverListener={tag && tag.tag_name !== ""}
              >
                <FbTypeography
                  variant={"caption"}
                  className={[classes.textTransform_none]}
                >
                  {searchText.length > 2
                    ? getHighlightedText(tag.tag_name, searchText)
                    : tag.tag_name}
                </FbTypeography>
              </FbTooltip>
            ))}
          </Space>
        );
      },
    },
  ];

  return (
    <FbInnerContainer
      contentBodyStyle={{ padding: 30 }}
      contentHeaderChildren={
        <FbGrid container className={classes.contentHeader}>
          <FbGrid item>
            <FbBox
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <FbIconButton onClick={() => history.goBack()}>
                <BackButton />
              </FbIconButton>
              <FbTypeography
                variant={"subtitle2"}
                className={classes.textTransform_none}
              >
                {name}
              </FbTypeography>
            </FbBox>
          </FbGrid>
        </FbGrid>
      }
    >
      <FbGrid container className={classes.contentBody}>
        <FbInput
          value={searchText}
          placeholder={"Search"}
          icon={SearchIcon}
          fullWidth={true}
          onChange={handleChange("searchText")}
        />
        <Table
          className={[classes.marginTop_25, classes.tableContainer]}
          style={{ width: "100%" }}
          tableLayout="auto"
          columns={columns}
          dataSource={rollskillMappingData}
          loading={isLoading}
          rowKey={(record) => record.id}
          pagination={false}
          size={"small"}
          scroll={{ x: 1000 }}
          onRow={(record, rowIndex) => {
            return {
              onClick: (event) => {
                addDeleteShowMoreTags(record.id);
              },
            };
          }}
          expandable={{
            expandRowByClick: false,
            expandedRowRender,    //  stable reference
            expandIcon,           //  stable reference
            rowExpandable: (record) => record.skills.length > 5,
            expandIconColumnIndex: 2,
          }}
        />
      </FbGrid>

      {/*  Replaced duplicate modal block with shared ConfigurationModal */}
      <ConfigurationModal
        showAddConfigModal={showAddConfigModal}
        setshowAddConfigModal={setshowAddConfigModal}
        disabletextInput={false}
        titleTag={titleTag}
        setTitleTag={setTitleTag}
        addEditConfig={addEditConfig}
        setaddEditConfig={setaddEditConfig}
        create_configutation={create_configutation}
        refreshTable={refreshTable}
      />
    </FbInnerContainer>
  );
}